import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import { Icon } from "../../../../../../elements";
import Alerts from "../../../../../../elements/v2/Alerts/Alerts";
import { useStore } from "../../../../../../hooks";
import LinearProgress from '@mui/material/LinearProgress';
import {
  SelectorContentWrapper,
  NameWrapper,
  AvatarWrapper,
  IndicatorsWrapper,
  RoleWrapper,
  DisplayWrapper,
  LoadingProgressWrapper,
  NameRoleWrapper,
} from "../UserSelector.styled";

const SimplifiedUserInfo = ({ userName, role }) => {
  return (
    <IndicatorsWrapper>
       {role ? (
           <NameRoleWrapper>
             <NameWrapper>{userName}:</NameWrapper>
             <RoleWrapper>{role}</RoleWrapper>
           </NameRoleWrapper>
        ) : (
          <NameWrapper>{userName}</NameWrapper>
        )}
    </IndicatorsWrapper>
  );
};


const UserDisplay = observer(({ defaultUser = {}, displayPhoto = true }) => {
  const { marketingStore } = useStore();
  const { advancedSettingsStore } = marketingStore;
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);
  
  const { userName, userRole } = useMemo(() => {
    if (!defaultUser) return { userName: "No owner assigned", userRole: "" };
    
    return {
      userName: defaultUser?.first_name
        ? `${defaultUser.first_name} ${defaultUser.last_name || ""}`
        : "No owner assigned",
      userRole: defaultUser?.role?.name || ""
    };
  }, [defaultUser?.first_name, defaultUser?.last_name, defaultUser?.role?.name]);

  useEffect(() => {
    const loadUserPhoto = async () => {
      if (!defaultUser?.id || !defaultUser?.display_photo_url || !advancedSettingsStore) return;
      
      try {
        const photoUrl = await advancedSettingsStore.loadUserPhoto(
          defaultUser.id,
          defaultUser.customer,
          defaultUser.display_photo_url
        );
        
        if (photoUrl) {
          setUserPhotoUrl(photoUrl);
        } else {
          setUserPhotoUrl(defaultUser?.photo_url || null);
        }
      } catch (error) {
        if (advancedSettingsStore) {
          advancedSettingsStore.isError = true;
          advancedSettingsStore.errorMessage = `Failed to load user photo`;
        }
        setUserPhotoUrl(defaultUser?.photo_url || null);
      }
    };
    
    loadUserPhoto();
  }, [defaultUser, advancedSettingsStore]);

  const showLinearProgress = (advancedSettingsStore?.isLoadingMultiSelectorItems || 
                           advancedSettingsStore?.isLoadingUserPhoto) && 
                           userName !== "No owner assigned";

  return (
    <>
      {advancedSettingsStore?.isError && (
        <Alerts
          severity="error"
          title="Error"
          message={advancedSettingsStore?.errorMessage}
          width="auto"
          top="61px"
          right="16px"
          onClose={() => advancedSettingsStore?.setIsError(false)}
        />
      )}
      <DisplayWrapper className="user-display-wrapper" selectedUser={defaultUser}>
        {displayPhoto && (
          <AvatarWrapper>
            {userPhotoUrl ? (
              <img src={userPhotoUrl} alt="User Avatar" />
            ) : (
              <div data-testid="mock-icon">
                <Icon name="carbonUserAvatarFilled" width="44px" height="44px" />
              </div>
            )}
          </AvatarWrapper>
        )}

        <SelectorContentWrapper>
          <SimplifiedUserInfo userName={userName} role={userRole} />
        </SelectorContentWrapper>
        
        {showLinearProgress && (
          <LoadingProgressWrapper>
            <LinearProgress />
          </LoadingProgressWrapper>
        )}
      </DisplayWrapper>
    </>
  );
});

UserDisplay.displayName = "UserDisplay";

export default UserDisplay;

SimplifiedUserInfo.propTypes = {
  userName: PropTypes.string.isRequired,
  role: PropTypes.string
};

SimplifiedUserInfo.defaultProps = {
  role: ''
};

UserDisplay.propTypes = {
  defaultUser: PropTypes.shape({
  id: PropTypes.string,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  role_group: PropTypes.string,
  customer: PropTypes.string,
  display_photo_url: PropTypes.string,
  photo_url: PropTypes.string
  })
};
